// React binding of the workspace: one workspace per (game, branch), kept in memory and in sessionStorage
// (spec/dashboard.md sections 4.1 and 4.6); the pages read drafts through `useWorkspaceVersion` / `useDraft`.
import { createContext, useContext, useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react';
import type { Json } from '../api/types';
import { registerDirtySource } from '../ui/leaveGuardRegistry';
import { entityKey, Workspace, type Draft, type EntityKind, type EntityKey, type WorkspaceSnapshot } from './Workspace';

const registry = new Map<string, Workspace>();
/** the pending sessionStorage writes (debounced), cleared with the registry so a test never writes a draft back after its reset */
const persistTimers = new Map<string, ReturnType<typeof setTimeout>>();

function storage(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

/** The workspace of a branch: the in-memory one, else the one sessionStorage kept, else a new one. */
export function workspaceFor(gameId: string, branch: string): Workspace {
  const id = `${gameId}:${branch}`;
  let w = registry.get(id);
  if (w) return w;
  const store = storage();
  const raw = store?.getItem(Workspace.storageKey(gameId, branch));
  if (raw) {
    try {
      const snapshot = JSON.parse(raw) as WorkspaceSnapshot;
      if (snapshot.version === 1 && snapshot.drafts.length > 0) w = Workspace.fromJSON(snapshot);
    } catch {
      w = undefined;
    }
  }
  w ??= new Workspace(gameId, branch);
  registry.set(id, w);
  w.subscribe(() => {
    const pending = persistTimers.get(id);
    if (pending) clearTimeout(pending);
    const timer = setTimeout(() => {
      persistTimers.delete(id);
      const s = storage();
      if (!s) return;
      try {
        if (w!.isDirty) s.setItem(Workspace.storageKey(gameId, branch), JSON.stringify(w!.toJSON()));
        else s.removeItem(Workspace.storageKey(gameId, branch));
      } catch {
        // quota or private mode: the drafts live in memory only
      }
    }, 300);
    persistTimers.set(id, timer);
  });
  return w;
}

/** Tests reset the registry between cases; a pending persistence write is dropped with it. */
export function resetWorkspaces(): void {
  for (const t of persistTimers.values()) clearTimeout(t);
  persistTimers.clear();
  registry.clear();
}

/** True while any workspace of any game, branch or environment holds drafts (the leave guard's check). */
export function anyWorkspaceDirty(): boolean {
  for (const w of registry.values()) if (w.isDirty) return true;
  return false;
}

// a reload or a tab close with drafts asks first (spec section 4.6); sessionStorage would survive a reload, not a close
registerDirtySource('workspaces', anyWorkspaceDirty);

/** Every dirty workspace of a game (the branch selector's "N unsaved" hints). */
export function dirtyBranches(gameId: string): { branch: string; count: number }[] {
  const out: { branch: string; count: number }[] = [];
  for (const w of registry.values()) if (w.gameId === gameId && w.isDirty) out.push({ branch: w.branch, count: w.changeCount });
  return out;
}

const WorkspaceContext = createContext<Workspace | null>(null);

export function WorkspaceProvider({ gameId, branch, children }: { gameId: string; branch: string; children: ReactNode }) {
  const workspace = useMemo(() => workspaceFor(gameId, branch), [gameId, branch]);
  useEffect(() => {
    // the restored-count banner shows once
    return () => {
      workspace.restoredCount = null;
    };
  }, [workspace]);
  return <WorkspaceContext.Provider value={workspace}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): Workspace {
  const w = useContext(WorkspaceContext);
  if (!w) throw new Error('useWorkspace must be used inside WorkspaceProvider');
  return w;
}

export function useOptionalWorkspace(): Workspace | null {
  return useContext(WorkspaceContext);
}

/** Re-renders on every change of the workspace; returns its version counter. */
export function useWorkspaceVersion(workspace: Workspace | null = useOptionalWorkspace()): number {
  return useSyncExternalStore(
    (listener) => (workspace ? workspace.subscribe(listener) : () => {}),
    () => (workspace ? workspace.getVersion() : 0),
    () => (workspace ? workspace.getVersion() : 0),
  );
}

export function useDraft(kind: EntityKind, id: string): Draft | undefined {
  const w = useWorkspace();
  useWorkspaceVersion(w);
  return w.getDraft(entityKey(kind, id));
}

/** The entity as the user sees it: the draft over the server value (undefined when deleted). */
export function useOverlay<T extends Json>(key: EntityKey, server: T | undefined): T | undefined {
  const w = useWorkspace();
  useWorkspaceVersion(w);
  return w.overlay(key, server);
}
